/* Copyright (c) 2020-2026 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { ReactElement } from "react";
import { When } from "react-if";
import {
  Button,
  ExtendedTooltip,
  FlexContainer,
  Text,
  theme,
} from "@nordcloud/gnui";
import {
  EventActionAttempt,
  useEventActionStatusChangedSubscription,
} from "~/generated/graphql";
import { ExpandIcon } from "~/components";
import { StyledSVGIcon } from "~/components/Styled/StyledSVGIcon";
import {
  ButtonText,
  ColorLabel,
  IconWrapper,
  ToggleButtonItem,
  ToggleItemBoxProps,
} from "~/components/ToggleItemBox";
import { useToggle } from "~/hooks";
import { isNotNil } from "~/tools";
import { SvgName } from "~/tools/types";
import { useEvent } from "../../EventsDetails/EventProvider";
import { useGetEventView } from "../../hooks";
import { EventActionItem } from "../../types";
import { EventAttemptToggleItemBox } from "./EventAttemptToggleItemBox";
import { Wrap } from "./styles";

type Props = ToggleItemBoxProps & {
  item: EventActionItem;
  attempts?: EventActionAttempt[];
  selectedAttempt?: EventActionAttempt;
  tag?: ReactElement;
};

export function EventToggleItemBox({
  item,
  attempts,
  selectedAttempt,
  active = false,
  tag,
}: Props) {
  const { event: eventCtx } = useEvent();

  const { refetch: refetchEvent } = useGetEventView({
    eventId: eventCtx.id,
  });

  const [isExpanded, toggleExpanded] = useToggle(false);
  const areAttempts = attempts && attempts.length > 1;

  useEventActionStatusChangedSubscription({
    variables: {
      input: {
        id: item.id,
      },
    },
    onData: () => {
      refetchEvent();
    },
  });

  return (
    <Wrap isNested={false} isExpanded={isExpanded} id={`action-${item.id}`}>
      <ToggleButtonItem
        color={item.color}
        active={active}
        onMouseDown={(e) => {
          item.onMouseDown?.();
          item.onSelect?.(undefined);
          e.stopPropagation();
        }}
      >
        <ColorLabel color={item.color} />
        <When condition={areAttempts}>
          <ExpandIcon
            name="chevronDown"
            title="chevronDown"
            isExpanded={isExpanded}
            onClick={toggleExpanded}
            css={{ margin: theme.spacing.spacing01 }}
          />
        </When>

        <When condition={isNotNil(item.icon)}>
          <IconWrapper>
            <ExtendedTooltip
              caption={item?.tooltip}
              status={item?.tooltipColor}
              position="start"
            >
              <StyledSVGIcon
                name={item.icon as SvgName}
                size="md"
                color={item.iconColor}
                pathColors={item.iconPathColors}
              />
            </ExtendedTooltip>
          </IconWrapper>
        </When>
        <ButtonText onMouseDown={item.onMouseDown}>
          <FlexContainer
            direction="column"
            alignItems="flex-start"
            mr={theme.spacing.spacing02}
          >
            <Text tag="div" align="left" mb={0} weight="medium">
              {item.name}
            </Text>
            <When condition={item.description != null}>
              <Text
                tag="div"
                mb={0}
                align="left"
                mt={theme.spacing.spacing01}
                size="sm"
              >
                {item.description}
              </Text>
            </When>
          </FlexContainer>
        </ButtonText>
        <When condition={!!tag}>{tag}</When>
        <When condition={item.onDelete != null}>
          <Button
            ml="auto"
            icon="trash"
            severity="low"
            size="md"
            onClick={item.onDelete}
          />
        </When>
      </ToggleButtonItem>
      <When condition={isExpanded}>
        {attempts?.map((attempt) => {
          const isAttemptSelected =
            active && selectedAttempt?.attempt === attempt.attempt;
          return (
            <EventAttemptToggleItemBox
              key={attempt.id}
              attempt={attempt}
              isAttemptSelected={isAttemptSelected}
              item={item}
            />
          );
        })}
      </When>
    </Wrap>
  );
}
